import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyFamilies } from "../../lib/api-family";
import type { FamilyListItem } from "../../types/family";
import { EmptyFamilyState } from "./EmptyFamilyState";
import { FamilyCard } from "./FamilyCard";
import { PENDING_INVITE_KEY } from "../family/JoinFamily";
import { UpcomingEventsPanel } from "./UpcomingEventsPanel";

type DashboardState =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "loaded"; families: FamilyListItem[] }
  | { status: "error" };

export function Dashboard() {
  const [state, setState] = useState<DashboardState>({ status: "loading" });
  const navigate = useNavigate();

  useEffect(() => {
    const pendingCode = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (pendingCode) {
      sessionStorage.removeItem(PENDING_INVITE_KEY);
      navigate(`/join?code=${encodeURIComponent(pendingCode)}`, {
        replace: true,
      });
      return;
    }

    let cancelled = false;
    getMyFamilies()
      .then((families) => {
        if (!cancelled) {
          setState(
            families.length === 0
              ? { status: "empty" }
              : { status: "loaded", families },
          );
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state.status === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#FF7A00] border-t-transparent" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <h2 className="mb-2 text-2xl font-bold tracking-[-0.03em] text-[#171714]">
          Не удалось загрузить
        </h2>
        <p className="mb-6 text-sm text-[#77776F]">
          Проверьте подключение к интернету
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="cursor-pointer rounded-full bg-[#171714] px-7 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          aria-label="Повторить загрузку"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (state.status === "empty") {
    return <EmptyFamilyState />;
  }

  return (
    <div className="px-5 pb-8 pt-6 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <div className="mb-7 lg:mb-10">
        <div>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF7A00]">Общий список</p>
          <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-[#171714] lg:text-[46px]">Моя семья</h1>
        </div>
      </div>
      <UpcomingEventsPanel />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 xl:gap-5">
        {state.families.map((f) => (
          <FamilyCard key={f.id} family={f} />
        ))}
      </div>
    </div>
  );
}
